import { useState, useRef } from 'react'
import { Job } from '../../types'
import { X, Upload, FileText } from 'lucide-react'
import { clsx } from 'clsx'

interface Props {
  job: Job
  onClose: () => void
  onSubmit: (coverLetter: string, resumeFile: File | null) => Promise<void>
}

export default function ApplyModal({ job, onClose, onSubmit }: Props) {
  const [coverLetter, setCoverLetter] = useState('')
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const isFree = job.referralFeePaise === 0
  const feeRupees = job.referralFeePaise / 100

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await onSubmit(coverLetter, resumeFile)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900">Apply for Referral</h2>
            <p className="text-sm text-gray-500 mt-0.5">{job.title} at {job.company.name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Fee notice */}
          <div className={clsx('rounded-lg p-3 text-sm', isFree ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700')}>
            {isFree
              ? 'This is a free referral — no fee will be charged.'
              : `A referral fee of ₹${feeRupees} will be held in escrow from your wallet until the employee confirms the referral.`}
          </div>

          {/* Cover letter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Cover Letter <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              rows={4}
              placeholder="Tell the employee why you're a great fit..."
              className="input resize-none"
            />
          </div>

          {/* Resume upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Resume</label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-200 rounded-lg p-4 text-sm text-gray-500 hover:border-primary-300 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
            >
              {resumeFile ? (
                <>
                  <FileText size={18} />
                  {resumeFile.name}
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Upload Resume (PDF, DOC)
                </>
              )}
            </button>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? 'Submitting...' : isFree ? 'Submit (Free)' : `Submit (₹${feeRupees})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
